import React, { useState } from 'react';
import { View, Text, Pressable, ScrollView, StyleSheet, Platform } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { addColorOpacity } from '../../shared/utils/styles';
import { ModuleCode, MODULE_CONFIG, BasePropertyType, isModuleApplicable } from '../../shared/types/due-diligence';
import { Ionicons } from '@expo/vector-icons';
import { Card } from '../ui';

export interface ModuleSelectorProps {
  selectedModules: ModuleCode[];
  onToggle: (module: ModuleCode) => void;
  propertyType: BasePropertyType;
  disabled?: boolean;
}

export function ModuleSelector({ 
  selectedModules, 
  onToggle,
  propertyType,
  disabled = false
}: ModuleSelectorProps) {
  const styles = useStyles();
  const { tokens, utils } = styles;
  const { isMobile, isSmallMobile } = useResponsive();
  const selectorStyles = makeSelectorStyles(tokens, utils, isMobile, isSmallMobile);

  // Safe array handling
  const safeSelectedModules = selectedModules || [];
  
  // Get all modules, filter by applicability
  const allModules = Object.values(MODULE_CONFIG).filter(m => m != null);
  const applicableModules = allModules.filter(
    module => module && isModuleApplicable(module.code, propertyType)
  );
  const requiredModules = applicableModules.filter(m => m && m.isAlwaysIncluded);
  const optionalModules = applicableModules.filter(m => m && !m.isAlwaysIncluded);

  const isModuleSelected = (code: ModuleCode) => safeSelectedModules.includes(code);
  const isModuleRequired = (code: ModuleCode) => MODULE_CONFIG[code]?.isAlwaysIncluded || false;

  return (
    <View style={selectorStyles.container}>
      <Text style={selectorStyles.label}>Checklist Modules</Text>
      <Text style={selectorStyles.description}>
        Select modules to include in your due diligence checklist
      </Text>

      {requiredModules.length > 0 && (
        <View style={selectorStyles.section}>
          <Text style={selectorStyles.sectionTitle}>Required</Text>
          <View style={selectorStyles.grid}>
            {requiredModules.map((module) => (
              <ModuleCard
                key={module.code}
                module={module}
                isSelected={isModuleSelected(module.code)}
                isRequired={true}
                onToggle={() => !disabled && onToggle(module.code)}
                disabled={disabled}
                styles={selectorStyles}
                tokens={tokens}
                utils={utils}
                isMobile={isMobile}
                isSmallMobile={isSmallMobile}
              />
            ))}
          </View>
        </View>
      )}

      {optionalModules.length > 0 && (
        <View style={selectorStyles.section}>
          <Text style={selectorStyles.sectionTitle}>Optional</Text>
          <View style={selectorStyles.grid}>
            {optionalModules.map((module) => (
              <ModuleCard
                key={module.code}
                module={module}
                isSelected={isModuleSelected(module.code)}
                isRequired={false}
                onToggle={() => !disabled && onToggle(module.code)}
                disabled={disabled}
                styles={selectorStyles}
                tokens={tokens}
                utils={utils}
                isMobile={isMobile}
                isSmallMobile={isSmallMobile}
              />
            ))}
          </View>
        </View>
      )}
    </View>
  );
}

interface ModuleCardProps {
  module: typeof MODULE_CONFIG[ModuleCode];
  isSelected: boolean;
  isRequired: boolean;
  onToggle: () => void;
  disabled: boolean;
  styles: ReturnType<typeof makeSelectorStyles>;
  tokens: ReturnType<typeof useStyles>['tokens'];
  utils: ReturnType<typeof useStyles>['utils'];
  isMobile: boolean;
  isSmallMobile: boolean;
}

function ModuleCard({
  module,
  isSelected,
  isRequired,
  onToggle,
  disabled,
  styles: selectorStyles,
  tokens,
  utils,
  isMobile,
  isSmallMobile,
}: ModuleCardProps) {
  return (
    <Pressable
      onPress={onToggle}
      disabled={disabled || isRequired}
      style={({ pressed }) => [
        selectorStyles.moduleCard,
        isSelected && selectorStyles.moduleCardSelected,
        (pressed || disabled) && selectorStyles.moduleCardPressed,
        (disabled || isRequired) && selectorStyles.moduleCardDisabled,
      ]}
    >
      <View style={selectorStyles.moduleContent}>
        <View style={selectorStyles.moduleHeader}>
          <View style={[
            selectorStyles.iconContainer,
            isSelected && selectorStyles.iconContainerSelected,
          ]}>
            <Ionicons
              name={module.icon as any}
              size={isMobile ? 20 : 24}
              color={isSelected ? tokens.color.primary : tokens.color.onSurface}
            />
          </View>
          {isSelected && (
            <View style={selectorStyles.checkmark}>
              <Ionicons
                name="checkmark-circle"
                size={18}
                color={tokens.color.primary}
              />
            </View>
          )}
          {isRequired && (
            <View style={selectorStyles.requiredBadge}>
              <Text style={selectorStyles.requiredText}>Required</Text>
            </View>
          )}
        </View>
        <Text style={[
          selectorStyles.moduleName,
          isSelected && selectorStyles.moduleNameSelected,
        ]}>
          {module.name}
        </Text>
        {module.description && !isSmallMobile && (
          <Text style={selectorStyles.moduleDescription} numberOfLines={2}>
            {module.description}
          </Text>
        )}
      </View>
    </Pressable>
  );
}

function makeSelectorStyles(
  tokens: ReturnType<typeof useStyles>['tokens'],
  utils: ReturnType<typeof useStyles>['utils'],
  isMobile: boolean,
  isSmallMobile: boolean
) {
  return StyleSheet.create({
    container: {
      width: '100%',
    },
    label: {
      fontSize: utils.typography.sizes.lg,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onBackground,
      marginBottom: utils.spacing.xs,
      fontFamily: utils.typography.heading.fontFamily,
    },
    description: {
      fontSize: utils.typography.sizes.sm,
      color: addColorOpacity(tokens.color.onSurface, 0.7),
      marginBottom: utils.spacing.lg,
      fontFamily: utils.typography.body.fontFamily,
    },
    section: {
      marginBottom: utils.spacing.xl,
    },
    sectionTitle: {
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onBackground,
      marginBottom: utils.spacing.md,
      fontFamily: utils.typography.body.fontFamily,
    },
    grid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: utils.spacing.md,
    },
    moduleCard: {
      flex: isMobile ? (isSmallMobile ? 1 : 1) : 0,
      minWidth: isMobile ? (isSmallMobile ? '48%' : '48%') : 200,
      maxWidth: isMobile ? (isSmallMobile ? '48%' : '48%') : 240,
      padding: utils.spacing.md,
      borderRadius: utils.radius.md,
      backgroundColor: tokens.color.surface,
      borderWidth: 2,
      borderColor: tokens.color.border,
      ...Platform.select({
        web: {
          cursor: 'pointer',
          transition: 'all 0.15s ease',
        } as any,
      }),
    },
    moduleCardSelected: {
      borderColor: tokens.color.primary,
      backgroundColor: addColorOpacity(tokens.color.primary, 0.05),
    },
    moduleCardPressed: {
      opacity: 0.7,
    },
    moduleCardDisabled: {
      opacity: 0.5,
    },
    moduleContent: {
      gap: utils.spacing.xs,
    },
    moduleHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: utils.spacing.xs,
    },
    iconContainer: {
      width: isMobile ? 36 : 40,
      height: isMobile ? 36 : 40,
      borderRadius: utils.radius.md,
      backgroundColor: addColorOpacity(tokens.color.onSurface, 0.1),
      alignItems: 'center',
      justifyContent: 'center',
    },
    iconContainerSelected: {
      backgroundColor: addColorOpacity(tokens.color.primary, 0.15),
    },
    checkmark: {
      marginLeft: 'auto',
    },
    requiredBadge: {
      paddingHorizontal: utils.spacing.xs,
      paddingVertical: 2,
      borderRadius: utils.radius.sm,
      backgroundColor: addColorOpacity(tokens.color.primary, 0.1),
    },
    requiredText: {
      fontSize: utils.typography.sizes.xs,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.primary,
      fontFamily: utils.typography.body.fontFamily,
    },
    moduleName: {
      fontSize: isSmallMobile ? utils.typography.sizes.sm : utils.typography.sizes.base,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
    },
    moduleNameSelected: {
      color: tokens.color.primary,
    },
    moduleDescription: {
      fontSize: utils.typography.sizes.xs,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      fontFamily: utils.typography.body.fontFamily,
      lineHeight: 16,
    },
  });
}

